// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from '@/auth/clerkShim';
import { toast } from "sonner";
import { publicSiteUrl } from '@/utils/publicSiteUrl';
import useAdminStore from '@store/adminStore';
import useOrganizationStore from '@store/organizationStore';
import {
  SPORT_NAMES,
  SPORT_COLOR_PALETTES,
  getColorPaletteList,
  getBannerTemplateList,
  getBannerFontList,
  getBannerStyleList,
  getDefaultColorKey,
  getDefaultTemplateKey,
  getDefaultFontKey,
  getDefaultStyleKey,
  resolveTemplateKey,
} from '@staty/shared/banners';
import { CUSTOM_PALETTE_KEY, normalizeCustomColors } from '@staty/shared/orgAppearance';
import TemplateBanner from '@staty/shared/components/TemplateBanner';
import '@staty/shared/styles/bannerTemplates.css';
import { TENANT_ROOT_DOMAIN } from '@staty/shared/domains';
import useGenerationJob from '../generation/useGenerationJob';
import VariantGrid from '../generation/VariantGrid';

interface BannerEditorProps {
  league: any;
  admin: any;
  /** Label for the primary action button. Defaults to "Save banner". */
  ctaLabel?: string;
  /** Called after the league settings are persisted. */
  onSaved?: (savedLeague: any) => void;
  /** Appearance-page mode (INT-0009): the editor renders inside a squished
   * 2/5 column with the page's own browser-framed live preview alongside —
   * single-column template grid, and the editor's internal Live Preview
   * section is dropped (it would duplicate the page-level one). */
  compact?: boolean;
  /** The hosting column is too narrow for multi-column grids (the Appearance
   * page's desktop-preview mode squeezes controls to ~3/10 width). */
  narrow?: boolean;
  /** Reports the UNSAVED selection so the page-level live preview can render
   * the banner draft: custom-build keys, or the picked AI variant's image
   * URL (generate mode). Null when nothing previewable is selected. */
  onDraftChange?: (
    draft:
      | { templateKey: string; colorKey: string; fontKey: string; logoPosition: 'left' | 'right' }
      | { imageUrl: string }
      | null,
  ) => void;
  /** Bump to trigger the editor's own save from OUTSIDE (the Appearance
   * page's sticky "Save banner" button, owner 2026-09-25). No-op while
   * nothing is saveable. Same signal pattern as LogoEditor. */
  externalSaveSignal?: number;
  /** Reports whether the editor could save right now (drives the external
   * button's disabled state). */
  onSaveableChange?: (state: { canSave: boolean; busy: boolean }) => void;
}

/**
 * Reusable banner editor — extracted from CreateOrganizationBannerPage so the
 * onboarding "Step 3 of 3" page and the League Settings → Appearance tab share
 * the same UI and behavior.
 *
 * Owns the editor state internally (mode, palette/template/font, file, upload
 * status) and persists changes via `useOrganizationStore.updateOrganization` when the
 * CTA is clicked. The image upload itself fires immediately on file select
 * via `useAdminStore.postLeagueBanner`, matching the original create flow.
 */
export default function BannerEditor({ league, admin, ctaLabel = 'Save banner', onSaved, compact = false, narrow = false, onDraftChange, externalSaveSignal = 0, onSaveableChange }: BannerEditorProps) {
  const { getToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [bannerMode, setBannerMode] = useState<'custom' | 'upload' | 'generate'>('custom');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);
  const [selectedColorKey, setSelectedColorKey] = useState<string>('');
  const [selectedFontKey, setSelectedFontKey] = useState<string>('');
  // Where the org logo sits inside the template banner (owner 2026-09-09).
  const [selectedLogoPosition, setSelectedLogoPosition] = useState<'left' | 'right'>('left');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  // AI-generate mode state
  const [selectedVibe, setSelectedVibe] = useState<'pro' | 'amateur' | 'social'>('pro');
  const [selectedStyle, setSelectedStyle] = useState<string>(getDefaultStyleKey());
  const [tagline, setTagline] = useState<string>('');
  const [promptAddendum, setPromptAddendum] = useState<string>('');
  const [useCustomColors, setUseCustomColors] = useState<boolean>(false);
  const [customPrimary, setCustomPrimary] = useState<string>('#0a4d4a');
  const [customSecondary, setCustomSecondary] = useState<string>('#d4a017');
  const [customAccent, setCustomAccent] = useState<string>('');
  const [selectedVariantUrl, setSelectedVariantUrl] = useState<string | null>(null);

  const postLeagueBanner = useAdminStore((state) => state.postLeagueBanner);
  const generateBanner = useAdminStore((state) => state.generateBanner);
  const getBannerJobStatus = useAdminStore((state) => state.getBannerJobStatus);
  const selectBannerVariant = useAdminStore((state) => state.selectBannerVariant);

  // Job lifecycle + polling live in the shared hook (2026-09-09 extraction —
  // LogoEditor shares it); popover state lives inside VariantGrid.
  const generation = useGenerationJob({
    startJob: async (input) => {
      const token = await getToken();
      return generateBanner(league._id, input, token);
    },
    getJobStatus: getBannerJobStatus,
    getToken,
    offlineMessage: 'Generation is not starting. The banner worker appears to be offline. Please try again later.',
  });

  const leagueSport = Array.isArray(league?.sports) && league.sports.length > 0 ? league.sports[0] : 'basketball';
  const colorPaletteList = getColorPaletteList(leagueSport);
  const bannerTemplateList = getBannerTemplateList();
  const bannerFontList = getBannerFontList();

  // Report the unsaved selection upward (page-level preview): custom-build
  // keys, or the picked AI variant's URL so the preview can show it pre-save.
  useEffect(() => {
    if (!onDraftChange) return;
    if (bannerMode === 'custom' && selectedTemplateKey && selectedColorKey) {
      onDraftChange({ templateKey: selectedTemplateKey, colorKey: selectedColorKey, fontKey: selectedFontKey, logoPosition: selectedLogoPosition });
    } else if (bannerMode === 'generate' && selectedVariantUrl) {
      onDraftChange({ imageUrl: selectedVariantUrl });
    } else {
      onDraftChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerMode, selectedTemplateKey, selectedColorKey, selectedFontKey, selectedVariantUrl, selectedLogoPosition]);

  // INT-0057: a site palette of 'custom' (Appearance → Color palette) is not
  // in the sport list; render the banner in the saved custom pair.
  const savedCustomColors = normalizeCustomColors(league?.settings?.banner?.customColors);
  const selectedColors = selectedColorKey === CUSTOM_PALETTE_KEY && savedCustomColors
    ? savedCustomColors
    : selectedColorKey
      ? (SPORT_COLOR_PALETTES[leagueSport]?.[selectedColorKey] || colorPaletteList[0])
      : colorPaletteList[0];

  // Initialize selections — prefer the league's persisted banner settings when present.
  useEffect(() => {
    const persisted = league?.settings?.banner || {};
    setBannerMode(persisted.bannerType === 'upload' ? 'upload' : 'custom');
    setSelectedColorKey(persisted.colorPaletteKey || getDefaultColorKey(leagueSport));
    setSelectedFontKey(persisted.fontKey || getDefaultFontKey());
    setSelectedLogoPosition(persisted.logoPosition === 'right' ? 'right' : 'left');
    // resolveTemplateKey maps retired legacy keys onto the 2026 template set.
    setSelectedTemplateKey(resolveTemplateKey(persisted.bannerTemplateKey || getDefaultTemplateKey()));
    if (persisted.bannerType === 'upload') {
      setUploadComplete(true);
    }
  }, [leagueSport, league?._id]);

  // Thumbnails render the SAME shared <TemplateBanner> component the public
  // site uses (getBannerSvg retired — templates are image-backed now).
  const templates = bannerTemplateList;

  useEffect(() => {
    if (!selectedFile) {
      setUploadedPreview(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setUploadedPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const uploadBannerImmediately = async (file: File) => {
    const token = await getToken();
    const leagueName = league?.name;
    if (!leagueName || !admin?._id) {
      // The admin slice loads asynchronously after sign-in; if it's still empty
      // when the user picks a file, the upload would silently no-op and the
      // "Save banner" button would stay disabled with no explanation.
      toast.error('Still loading your account. Please try again in a moment.');
      setSelectedFile(null);
      return;
    }

    setIsUploading(true);
    setUploadComplete(false);

    postLeagueBanner(file, leagueName, admin._id, token, (result) => {
      setIsUploading(false);
      // Only flip "Uploaded ✓" when the upload actually succeeded. Failures
      // pass `result = null` so the spinner clears but the green check and
      // the auto-save below stay gated.
      if (result) {
        setUploadComplete(true);
        setIframeKey((prev) => prev + 1);
        // Auto-persist `bannerType: 'upload'` so the user doesn't have to
        // find the distant "Save banner" button at the bottom. Custom + generate
        // modes still rely on the explicit Save click (they have downstream
        // selections that need an "I'm done" confirmation).
        if (bannerMode === 'upload') {
          handleSave();
        }
      }
    });
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setSelectedTemplateKey(null);
    await uploadBannerImmediately(file);
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setSelectedTemplateKey(null);
      await uploadBannerImmediately(file);
    }
  };

  const onSelectTemplate = (key: string) => {
    setSelectedTemplateKey(key);
  };

  const onRemoveUpload = () => {
    setSelectedFile(null);
    setUploadedPreview(null);
    setUploadComplete(false);
  };

  const isValidHex = (v: string) => /^#[0-9a-f]{6}$/i.test(v);

  type GenerateOverrides = { styleOverride?: string; addendumExtra?: string };

  const buildGenerateInput = (basedOnUrl?: string | null, overrides?: GenerateOverrides) => {
    const primary = useCustomColors ? customPrimary : selectedColors?.primary;
    const secondary = useCustomColors ? customSecondary : selectedColors?.secondary;
    if (!primary || !secondary) return null;
    const palette: any = { primary, secondary };
    if (useCustomColors && customAccent && isValidHex(customAccent)) {
      palette.accent = customAccent;
    }
    const effectiveStyle = overrides?.styleOverride || selectedStyle;
    const combinedAddendum = [promptAddendum.trim(), overrides?.addendumExtra?.trim()]
      .filter(Boolean)
      .join('. ');
    const input: any = {
      orgName: league.displayName || league.name,
      sport: leagueSport,
      vibe: selectedVibe,
      style: effectiveStyle,
      palette,
    };
    if (tagline.trim()) input.tagline = tagline.trim();
    if (combinedAddendum) input.promptAddendum = combinedAddendum.slice(0, 200);
    if (basedOnUrl) input.basedOnUrl = basedOnUrl;
    return input;
  };

  const startGeneration = async (basedOnUrl: string | null, overrides?: GenerateOverrides) => {
    // Never fire with no org id — a click before the org resolved used to POST
    // to /organizations/undefined/banners (prod 500, bug alert 2026-08-23).
    // Tell the user instead of silently ignoring the click.
    if (!league?._id) {
      generation.fail('Your organization is still loading. Try again in a moment.', 'idle');
      return;
    }
    if (useCustomColors && (!isValidHex(customPrimary) || !isValidHex(customSecondary))) {
      generation.fail('Custom colors must be valid 6-digit hex values (e.g. #0a4d4a).');
      return;
    }
    const input = buildGenerateInput(basedOnUrl, overrides);
    if (!input) return;

    setSelectedVariantUrl(null);
    await generation.start(input);
  };

  const onGenerateBanners = () => startGeneration(null);

  const handleSave = async () => {
    const leagueName = league?.name;
    // league._id included: saving with a half-loaded org would PUT to an
    // /organizations/undefined URL (same class as the 2026-08-23 bug alert).
    if (!leagueName || !league?._id || !admin?._id) return;

    setLoading(true);
    const updateOrganization = useOrganizationStore.getState().updateOrganization(getToken);

    try {
      let saved;
      if (bannerMode === 'custom') {
        if (!selectedTemplateKey) {
          setLoading(false);
          return;
        }
        saved = await updateOrganization({
          _id: league._id,
          settings: {
            ...league.settings,
            banner: {
              bannerType: 'custom',
              colorPaletteKey: selectedColorKey,
              bannerTemplateKey: selectedTemplateKey,
              fontKey: selectedFontKey,
              logoPosition: selectedLogoPosition,
              // INT-0057: never drop the site's custom pair on a banner save.
              ...(savedCustomColors ? { customColors: savedCustomColors } : {}),
            },
          },
        });
      } else if (bannerMode === 'upload') {
        saved = await updateOrganization({
          _id: league._id,
          settings: {
            ...league.settings,
            banner: {
              bannerType: 'upload',
            },
          },
        });
      } else {
        // generate mode
        if (!selectedVariantUrl) {
          setLoading(false);
          return;
        }
        const token = await getToken();
        await selectBannerVariant(league._id, selectedVariantUrl, token);
        saved = await updateOrganization({
          _id: league._id,
          settings: {
            ...league.settings,
            banner: {
              bannerType: 'upload',
            },
          },
        });
        setIframeKey((prev) => prev + 1);
      }
      onSaved?.(saved);
    } finally {
      setLoading(false);
    }
  };

  const canSave = bannerMode === 'custom'
    ? selectedTemplateKey !== null
    : bannerMode === 'upload'
      ? uploadComplete
      : selectedVariantUrl !== null;

  // External save trigger + saveable reporting (owner 2026-09-25): the
  // Appearance page's sticky Banner header button drives handleSave from
  // outside; skip the mount value so only real bumps save.
  const lastExternalSave = useRef(externalSaveSignal);
  useEffect(() => {
    if (externalSaveSignal === lastExternalSave.current) return;
    lastExternalSave.current = externalSaveSignal;
    if (canSave && !loading && !isUploading) handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSaveSignal]);
  useEffect(() => {
    onSaveableChange?.({ canSave, busy: loading || isUploading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSave, loading, isUploading]);

  const leagueSlug = league?.home || league?.name || '';
  // Env-aware base URL (runtime-host detection) via the shared helper — see
  // publicSiteUrl.ts for why build flags can't be trusted here.
  const previewQueryParams = `?sports_color=${leagueSport}_${selectedColorKey}&banner_template=${selectedTemplateKey ?? getDefaultTemplateKey()}&banner_font=${selectedFontKey || getDefaultFontKey()}&banner_logo=${selectedLogoPosition}`;

  const previewUrl = leagueSlug
    ? publicSiteUrl(
        leagueSlug,
        bannerMode === 'upload' && uploadComplete
          ? `/home?t=${iframeKey}`
          : `/home${previewQueryParams}`,
      )
    : '';

  return (
    <div className="flex flex-col gap-8">
      {/* Mode Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-2xl bg-muted p-1.5">
          <button
            type="button"
            onClick={() => setBannerMode('custom')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 ${
              bannerMode === 'custom'
                ? 'bg-card text-foreground shadow-md'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
            </svg>
            Build Custom
          </button>
          <button
            type="button"
            onClick={() => setBannerMode('upload')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 ${
              bannerMode === 'upload'
                ? 'bg-card text-foreground shadow-md'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Upload Your Own
          </button>
          <button
            type="button"
            onClick={() => setBannerMode('generate')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 ${
              bannerMode === 'generate'
                ? 'bg-card text-foreground shadow-md'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l1.5 3L9 7.5 6.5 9 5 12 3.5 9 1 7.5 3.5 6 5 3zm14 0l1.5 3L23 7.5 20.5 9 19 12 17.5 9 15 7.5 17.5 6 19 3zM12 9l2.5 5L20 16.5 14.5 19 12 24 9.5 19 4 16.5 9.5 14 12 9z" />
            </svg>
            Generate with AI
          </button>
        </div>
      </div>

      {/* Custom Banner Mode */}
      {bannerMode === 'custom' && (
        <>
          {/* Color Palette Selection */}
          <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Color palette</h2>
              <span className="text-sm text-muted-foreground">{SPORT_NAMES[leagueSport] || 'Basketball'} colors</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {colorPaletteList.map((palette) => {
                const isSelected = selectedColorKey === palette.key;
                return (
                  <button
                    key={`palette-${palette.key}`}
                    type="button"
                    onClick={() => setSelectedColorKey(palette.key)}
                    className={`group relative flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-200 ${
                      isSelected
                        ? 'bg-foreground text-white shadow-lg shadow-slate-900/20'
                        : 'bg-muted/50 text-foreground hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <div className="h-7 w-7 rounded-lg ring-2 ring-white/50" style={{ backgroundColor: palette.primary }} />
                      <div className="h-7 w-7 rounded-lg ring-2 ring-white/50" style={{ backgroundColor: palette.secondary }} />
                    </div>
                    <span className={`text-sm font-medium capitalize ${isSelected ? 'text-white' : 'text-muted-foreground'}`}>
                      {palette.key.replace(/_/g, ' ')}
                    </span>
                    {isSelected && (
                      <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-success shadow-sm">
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Font Selection */}
          <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Font style</h2>
              <span className="text-sm text-muted-foreground">Choose a typeface</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {bannerFontList.map((font) => {
                const isSelected = selectedFontKey === font.key;
                return (
                  <button
                    key={`font-${font.key}`}
                    type="button"
                    onClick={() => setSelectedFontKey(font.key)}
                    className={`group relative rounded-2xl px-5 py-3 transition-all duration-200 ${
                      isSelected
                        ? 'bg-foreground text-white shadow-lg shadow-slate-900/20'
                        : 'bg-muted/50 text-foreground hover:bg-muted'
                    }`}
                  >
                    <span
                      className={`text-lg font-bold ${isSelected ? 'text-white' : 'text-foreground'}`}
                      style={{ fontFamily: font.family }}
                    >
                      {font.name}
                    </span>
                    {isSelected && (
                      <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-success shadow-sm">
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Logo position — only offered when the org actually has a logo.
              The logo lives INSIDE the banner on the simple template (owner
              2026-09-09); the header logo yields to it. */}
          {league?.hasLogo && (
            <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border" data-testid="banner-logo-position">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-foreground">Logo position</h2>
                <span className="text-sm text-muted-foreground">Your logo appears in the banner</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {(['left', 'right'] as const).map((pos) => {
                  const isSelected = selectedLogoPosition === pos;
                  return (
                    <button
                      key={`logo-pos-${pos}`}
                      type="button"
                      onClick={() => setSelectedLogoPosition(pos)}
                      className={`group relative rounded-2xl px-5 py-3 transition-all duration-200 ${
                        isSelected
                          ? 'bg-foreground text-white shadow-lg shadow-slate-900/20'
                          : 'bg-muted/50 text-foreground hover:bg-muted'
                      }`}
                    >
                      <span className={`text-sm font-semibold capitalize ${isSelected ? 'text-white' : 'text-foreground'}`}>
                        {pos}
                      </span>
                      {isSelected && (
                        <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-success shadow-sm">
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Banner Template Selection */}
          <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Banner template</h2>
              <span className="text-sm text-muted-foreground">Select a design</span>
            </div>
            <div className={compact ? 'grid gap-4 grid-cols-1' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'}>
              {templates.map((template) => {
                const isSelected = selectedTemplateKey === template.key;
                return (
                  <button
                    key={`banner-${template.key}`}
                    type="button"
                    onClick={() => onSelectTemplate(template.key)}
                    className={`group relative overflow-hidden rounded-2xl p-3 transition-all duration-200 ${
                      isSelected
                        ? 'bg-foreground shadow-lg shadow-slate-900/20 ring-2 ring-foreground'
                        : 'bg-muted/50 hover:bg-muted ring-1 ring-border hover:ring-input'
                    }`}
                  >
                    <div className="overflow-hidden rounded-xl bg-white">
                      <TemplateBanner
                        sport={leagueSport}
                        templateKey={template.key}
                        colors={selectedColors}
                        fontKey={selectedFontKey}
                        orgName={league?.displayName}
                      />
                    </div>
                    <div className={`mt-2 text-center text-sm font-medium ${isSelected ? 'text-white' : 'text-muted-foreground'}`}>
                      {template.name}
                    </div>
                    {isSelected && (
                      <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-success shadow-sm">
                        <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        </>
      )}

      {/* Upload Banner Mode */}
      {bannerMode === 'upload' && (
        <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.svg"
            onChange={onFileChange}
            className="hidden"
          />

          {uploadedPreview ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-semibold text-foreground">Your banner</h2>
                  {isUploading && (
                    <div className="flex items-center gap-2 text-sm text-warning">
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Uploading...
                    </div>
                  )}
                  {uploadComplete && !isUploading && (
                    <div className="flex items-center gap-1.5 text-sm text-success">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Uploaded
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onRemoveUpload}
                  disabled={isUploading}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove
                </button>
              </div>
              <div className="overflow-hidden rounded-2xl bg-muted/50 p-4 ring-1 ring-border">
                <img src={uploadedPreview} alt="Uploaded banner preview" className="mx-auto h-[100px] object-contain" />
              </div>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all duration-200 ${
                isDragging
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-muted/50 hover:border-input hover:bg-muted'
              }`}
            >
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-card shadow-sm ring-1 ring-border">
                <svg className="h-7 w-7 text-muted-foreground/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-base font-medium text-foreground">
                Drop your image here, or <span className="text-primary">browse</span>
              </p>
              <p className="mt-1 text-sm text-muted-foreground/60">
                PNG, JPG, SVG up to 5MB
              </p>
              <p className="mt-2 text-xs text-muted-foreground/60">
                Recommended size: <span className="font-medium text-muted-foreground">600 x 145px</span>
              </p>
            </div>
          )}
        </section>
      )}

      {/* Generate with AI Mode */}
      {bannerMode === 'generate' && (
        <>
          {/* Style preset */}
          <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Style</h2>
              <span className="text-sm text-muted-foreground">Overall visual look</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {getBannerStyleList().map((style) => {
                const isSelected = selectedStyle === style.key;
                return (
                  <button
                    key={`style-${style.key}`}
                    type="button"
                    onClick={() => setSelectedStyle(style.key)}
                    className={`group relative flex flex-col items-start gap-0.5 rounded-2xl px-4 py-3 text-left transition-all duration-200 ${
                      isSelected
                        ? 'bg-foreground text-white shadow-lg shadow-slate-900/20'
                        : 'bg-muted/50 text-foreground hover:bg-muted'
                    }`}
                  >
                    <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-foreground'}`}>
                      {style.name}
                    </span>
                    <span className={`text-xs ${isSelected ? 'text-white/70' : 'text-muted-foreground'}`}>
                      {style.description}
                    </span>
                    {isSelected && (
                      <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-success shadow-sm">
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Sport + Vibe */}
          <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Vibe</h2>
              <span className="text-sm text-muted-foreground">{SPORT_NAMES[leagueSport] || 'Basketball'}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {(['pro', 'amateur', 'social'] as const).map((vibe) => {
                const isSelected = selectedVibe === vibe;
                return (
                  <button
                    key={`vibe-${vibe}`}
                    type="button"
                    onClick={() => setSelectedVibe(vibe)}
                    className={`group relative rounded-2xl px-5 py-3 transition-all duration-200 ${
                      isSelected
                        ? 'bg-foreground text-white shadow-lg shadow-slate-900/20'
                        : 'bg-muted/50 text-foreground hover:bg-muted'
                    }`}
                  >
                    <span className={`text-sm font-semibold capitalize ${isSelected ? 'text-white' : 'text-foreground'}`}>
                      {vibe}
                    </span>
                    {isSelected && (
                      <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-success shadow-sm">
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Color palette (reused from custom mode) */}
          <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Color palette</h2>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={useCustomColors}
                  onChange={(e) => setUseCustomColors(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                Use custom hex colors
              </label>
            </div>

            {!useCustomColors && (
              <div className="flex flex-wrap gap-3">
                {colorPaletteList.map((palette) => {
                  const isSelected = selectedColorKey === palette.key;
                  return (
                    <button
                      key={`gen-palette-${palette.key}`}
                      type="button"
                      onClick={() => setSelectedColorKey(palette.key)}
                      className={`group relative flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-200 ${
                        isSelected
                          ? 'bg-foreground text-white shadow-lg shadow-slate-900/20'
                          : 'bg-muted/50 text-foreground hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <div className="h-7 w-7 rounded-lg ring-2 ring-white/50" style={{ backgroundColor: palette.primary }} />
                        <div className="h-7 w-7 rounded-lg ring-2 ring-white/50" style={{ backgroundColor: palette.secondary }} />
                      </div>
                      <span className={`text-sm font-medium capitalize ${isSelected ? 'text-white' : 'text-muted-foreground'}`}>
                        {palette.key.replace(/_/g, ' ')}
                      </span>
                      {isSelected && (
                        <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-success shadow-sm">
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {useCustomColors && (
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { label: 'Primary', value: customPrimary, onChange: setCustomPrimary, required: true },
                  { label: 'Secondary', value: customSecondary, onChange: setCustomSecondary, required: true },
                  { label: 'Accent (optional)', value: customAccent, onChange: setCustomAccent, required: false },
                ].map((field) => (
                  <label key={field.label} className="block">
                    <span className="mb-1.5 block text-sm font-medium text-foreground">{field.label}</span>
                    <div className="flex items-center gap-2 rounded-xl bg-muted/50 px-3 py-2 ring-1 ring-border">
                      <input
                        type="color"
                        value={isValidHex(field.value) ? field.value : '#000000'}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                      />
                      <input
                        type="text"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder={field.required ? '#0a4d4a' : '(none)'}
                        className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
                      />
                    </div>
                  </label>
                ))}
              </div>
            )}
          </section>

          {/* Tagline + advanced direction */}
          <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Text &amp; direction</h2>
              <span className="text-sm text-muted-foreground">Optional</span>
            </div>
            <div className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">Tagline</span>
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value.slice(0, 80))}
                  placeholder="e.g. Where champions are made"
                  className="w-full rounded-xl border-0 bg-muted/50 px-4 py-3 text-sm text-foreground ring-1 ring-border placeholder:text-muted-foreground/60 focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <span className="mt-1 block text-xs text-muted-foreground/60">Rendered below the org name on the banner. Up to 80 characters.</span>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">Extra direction for the AI</span>
                <textarea
                  rows={2}
                  value={promptAddendum}
                  onChange={(e) => setPromptAddendum(e.target.value.slice(0, 200))}
                  placeholder="e.g. add basketball hoop silhouette, sunset gradient"
                  className="w-full rounded-xl border-0 bg-muted/50 px-4 py-3 text-sm text-foreground ring-1 ring-border placeholder:text-muted-foreground/60 focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <span className="mt-1 block text-xs text-muted-foreground/60">Free-text hint appended to the prompt. Up to 200 characters.</span>
              </label>
            </div>
          </section>

          {/* Generate action + status + variants */}
          <section className="rounded-[28px] bg-card p-6 shadow-xl shadow-slate-200/50 ring-1 ring-border">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Banner variants</h2>
              <button
                type="button"
                onClick={onGenerateBanners}
                disabled={generation.jobState === 'waiting' || generation.jobState === 'active'}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-primary-deep px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {generation.jobState === 'waiting' || generation.jobState === 'active' ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating{generation.stage ? `… ${generation.stage}` : '…'}
                  </>
                ) : generation.variants.length > 0 ? 'Regenerate' : 'Generate banners'}
              </button>
            </div>

            {generation.error && (
              <div className="mb-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive ring-1 ring-destructive/30">
                {generation.error}
              </div>
            )}

            {generation.variants.length === 0 && generation.jobState === 'idle' && (
              <p className="text-sm text-muted-foreground">
                Pick a vibe and palette, then generate 3 banner variants to choose from. Generation takes ~10–30 seconds.
              </p>
            )}

            <VariantGrid
              variants={generation.variants}
              selectedUrl={selectedVariantUrl}
              onSelect={setSelectedVariantUrl}
              onMoreLikeThis={(variantUrl, overrides) => startGeneration(variantUrl, overrides)}
              busy={generation.jobState === 'waiting' || generation.jobState === 'active'}
              narrow={narrow}
              styleOptions={getBannerStyleList()}
              defaultStyleKey={selectedStyle}
            />
          </section>
        </>
      )}

      {/* Live Preview — page-level preview replaces this in compact mode */}
      {!compact && (
<section className="rounded-[28px] bg-card p-5 shadow-xl shadow-slate-200/50 ring-1 ring-border">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground">Live preview</h2>
            <p className="mt-0.5 text-sm text-muted-foreground/60">Don't worry, everything here is editable later</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="flex h-2 w-2 animate-pulse rounded-full bg-success" />
            Real-time
          </div>
        </div>
        <div className="overflow-hidden rounded-2xl bg-muted ring-1 ring-border">
          <div className="flex items-center gap-2 bg-muted-foreground/10 px-4 py-2.5">
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-destructive" />
              <span className="h-3 w-3 rounded-full bg-warning" />
              <span className="h-3 w-3 rounded-full bg-success" />
            </div>
            <div className="ml-2 flex-1 rounded-lg bg-card px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
              {previewUrl || `https://your-league.${TENANT_ROOT_DOMAIN}`}
            </div>
          </div>
          <div className="bg-white">
            {previewUrl ? (
              <iframe
                key={iframeKey}
                title="Site preview"
                src={previewUrl}
                className="h-[400px] w-full border-0"
              />
            ) : (
              <div className="flex h-[400px] items-center justify-center text-sm text-muted-foreground/60">
                <div className="text-center">
                  <svg className="mx-auto mb-3 h-10 w-10 text-muted-foreground/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Preview unavailable
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave || loading || isUploading}
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-r from-foreground via-foreground/90 to-foreground px-8 py-4 text-base font-semibold text-white shadow-xl shadow-slate-900/25 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-900/30 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="relative z-10 flex items-center gap-2">
            {loading ? (
              <>
                <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                {ctaLabel}
                <svg className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </>
            )}
          </span>
          <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
        </button>
      </div>
    </div>
  );
}
